import { AppNode } from "@features/builder/graph/types"
import { groupPadding, groupTitleHeight } from "@features/builder/helpers/nodes"
import { nodesGroupMapper } from "@features/builder/helpers/nodesGroupMapper"

/**
 * Resizes parent groups to fit their child groups if child is bigger than its new parent.
 * For each selected node, checks if its parent group is too small to contain it.
 * Expands parent horizontally or vertically as needed, and updates child position from absolute to relative to stay inside parent.
 * Returns updated nodes array with new sizes and positions.
 *
 * @param selectedNodes - Nodes that are dragged into new group
 * @param allNodes - All nodes in the graph
 * @returns updated AppNodes
 */
export function resizeGroupParents({
  selectedNodes,
  allNodes,
}: {
  selectedNodes: AppNode[]
  allNodes: AppNode[]
}) {
  const mapper = nodesGroupMapper(structuredClone(allNodes))

  for (const node of selectedNodes) {
    const child = mapper.groupsMap.get(node.id)
    if (!child) continue

    const parents = mapper.childToParentGroupsIdsMapDeep.get(child.id)
    if (!parents) continue

    for (const pId of parents) {
      const parent = mapper.allMap.get(pId)
      if (!parent) continue

      // Mutate updatedParent and updatedChild so width and height changes can be chained.
      // Only set in mapper.allMap after both updates, otherwise only one change applies.
      // This ensures both dimensions are resized together if needed.
      let updatedParent = structuredClone(parent)
      let updatedChild = structuredClone(child) as AppNode

      if (child.position.x < groupPadding) {
        const incWidth = increaseWidth(updatedParent, updatedChild)
        updatedParent = incWidth.parent
        updatedChild = incWidth.child
      }

      if (child.position.y < groupPadding + groupTitleHeight) {
        const incHeight = increaseHeight(updatedParent, updatedChild)
        updatedParent = incHeight.parent
        updatedChild = incHeight.child
      }

      mapper.allMap.set(parent.id, updatedParent)
      mapper.allMap.set(child.id, updatedChild)
    }
  }

  const updatedNodes = [...mapper.allMap.values()]

  return updatedNodes
}

/**
 * Expands parent group vertically if child is outside parent's top bound.
 * Updates child node's y position to keep it inside parent's new bounds after resizing.
 * Returns updated parent and child nodes with new size and position.
 *
 * @param parent - The parent group node to resize
 * @param child - The child group node to reposition
 * @returns Updated nodes
 */
function increaseHeight(parent: AppNode, child: AppNode) {
  const deltaY = child.position.y

  const newPY = parent.position.y + deltaY - groupPadding - groupTitleHeight
  const newPH = Number(parent.style?.height) - deltaY + groupPadding + groupTitleHeight

  const updatedParent = structuredClone(parent)
  updatedParent.style = { ...parent.style, height: newPH }
  if (updatedParent.data.nestingLevel === 1) {
    updatedParent.position.y = newPY
  }

  const updatedChild = structuredClone(child)
  updatedChild.position.y = child.position.y - deltaY + groupPadding + groupTitleHeight

  return { parent: updatedParent, child: updatedChild }
}

/**
 * Expands parent group horizontally if child is outside parent's left bound.
 * Updates child node's x position to keep it inside parent's new bounds after resizing.
 * Returns updated parent and child nodes with new size and position.
 *
 * @param parent - The parent group node to resize
 * @param child - The child group node to reposition
 * @returns Updated nodes
 */
function increaseWidth(parent: AppNode, child: AppNode) {
  const deltaX = child.position.x

  const newPX = parent.position.x + deltaX - groupPadding
  const newPW = Number(parent.style?.width) - deltaX + groupPadding

  const updatedParent = structuredClone(parent)
  updatedParent.style = { ...parent.style, width: newPW }
  if (updatedParent.data.nestingLevel === 1) {
    updatedParent.position.x = newPX
  }

  const updatedChild = structuredClone(child)
  updatedChild.position.x = child.position.x - deltaX + groupPadding

  return { parent: updatedParent, child: updatedChild }
}
